import {FACE_FINDER_AVAILABLE, FACE_ASSET_DIRECTORY} from './faceFinderAvailability';

export const MIN_FACE_CONFIDENCE = 0.6;
// These are bundled public assets, not CDN imports. Nothing is fetched until On.
export async function loadMediaPipe(importVision = url => import(/* webpackIgnore: true */ url)) {
  const prefix = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  const base = new URL(`${prefix}/${FACE_ASSET_DIRECTORY}/`, window.location.origin);
  if (base.origin !== window.location.origin) throw new Error('Face assets must be same-origin');
  const vision = await importVision(new URL('vision_bundle.js', base).href);
  const files = await vision.FilesetResolver.forVisionTasks(new URL('wasm', base).href);
  return vision.FaceDetector.createFromOptions(files, {
    baseOptions: {modelAssetPath: new URL('blaze_face_short_range.tflite', base).href, delegate: 'CPU'},
    runningMode: 'IMAGE', minDetectionConfidence: MIN_FACE_CONFIDENCE, minSuppressionThreshold: 0.3
  });
}
function deadline(promise, ms) {
  let timer;
  return Promise.race([promise, new Promise((_, reject) => {timer = setTimeout(() => reject(new Error('Face loading timed out')), ms);})]).finally(() => clearTimeout(timer));
}
export function createFaceFinder({enabled = FACE_FINDER_AVAILABLE, load = loadMediaPipe, timeoutMs = 12000} = {}) {
  let detectorPromise;
  return async image => {
    if (!enabled) return {faces: [], reason: 'unavailable'};
    if (!image?.complete || !image.naturalWidth || !image.naturalHeight) return {faces: [], reason: 'unavailable'};
    let detector;
    try {
      if (!detectorPromise) detectorPromise = Promise.resolve().then(load).catch(error => {detectorPromise = null; throw error;});
      detector = await deadline(detectorPromise, timeoutMs);
      // One inference on the EXIF-oriented still image; no camera/video loop.
      const result = detector.detect(image);
      const faces = (result.detections || []).filter(d => d.categories?.some(c => Number.isFinite(c.score) && c.score >= MIN_FACE_CONFIDENCE && c.score <= 1))
        .map(d => ({x: d.boundingBox?.originX, y: d.boundingBox?.originY, width: d.boundingBox?.width, height: d.boundingBox?.height}))
        .filter(b => Object.values(b).every(Number.isFinite) && b.width > 0 && b.height > 0);
      return {faces, reason: faces.length ? 'detected' : 'none'};
    } catch (_) {
      if (detector) {try {detector.close?.();} catch (_) {} detectorPromise = null;}
      // Cover remains ready. Do not log images, landmarks or private photo data.
      return {faces: [], reason: 'unavailable'};
    }
  };
}
export const detectFaces = createFaceFinder();
